import { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useInView } from "framer-motion";

// =============================================================================
// Types & Data
// =============================================================================

interface DocSection {
  id: string;
  title: string;
  items: { id: string; title: string; content: string }[];
}

const DOC_SECTIONS: DocSection[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    items: [
      {
        id: "introduction",
        title: "Introduction",
        content: `# Introduction to HVME

HVME provides privacy-preserving AI infrastructure that keeps your data protected throughout the entire workflow.

## Key Concepts

- **Encrypted Compute**: Process sensitive data without exposing it
- **Agent Mesh**: Distributed network of AI agents with secure communication
- **Privacy Modes**: Configure data retention and security policies

## Quick Start

\`\`\`bash
npm install @hvme/sdk
\`\`\`

\`\`\`typescript
import { HVME } from '@hvme/sdk';

const client = new HVME({
  apiKey: process.env.HVME_API_KEY,
  privacyMode: 'high-sensitivity'
});

const result = await client.compute({
  model: 'gpt-4',
  input: data
});
\`\`\``,
      },
      {
        id: "installation",
        title: "Installation",
        content: `# Installation

## Requirements

- Node.js 18+ or Bun 1.0+
- TypeScript 5.0+ (recommended)

## Package Installation

\`\`\`bash
# npm
npm install @hvme/sdk @hvme/crypto

# yarn
yarn add @hvme/sdk @hvme/crypto

# pnpm
pnpm add @hvme/sdk @hvme/crypto
\`\`\`

## Environment Setup

Create a \`.env\` file:

\`\`\`env
HVME_API_KEY=your_api_key_here
HVME_ENDPOINT=https://api.hvme.ai
\`\`\``,
      },
      {
        id: "quickstart",
        title: "Quick Start Guide",
        content: `# Quick Start Guide

Get up and running in 5 minutes.

## 1. Create an Account

Visit the console and sign up for a free account.

## 2. Get Your API Key

Navigate to Settings → API Keys → Create New Key

## 3. Initialize the Client

\`\`\`typescript
import { HVME } from '@hvme/sdk';

const hvme = new HVME({
  apiKey: process.env.HVME_API_KEY
});
\`\`\`

## 4. Run Your First Computation

\`\`\`typescript
const session = await hvme.createSession({
  privacyMode: 'standard'
});

const result = await session.compute({
  operation: 'analyze',
  data: { text: 'Hello, world!' }
});

console.log(result);
\`\`\``,
      },
    ],
  },
  {
    id: "core-concepts",
    title: "Core Concepts",
    items: [
      {
        id: "privacy-modes",
        title: "Privacy Modes",
        content: `# Privacy Modes

HVME offers three privacy modes to match your security requirements.

## Standard Mode

- 1-hour scratch memory TTL
- Pinning allowed
- Remote storage enabled
- Full agent mesh access

Best for: Development, non-sensitive workloads

## High Sensitivity Mode

- 5-minute scratch memory TTL
- Pinning disabled
- Device-only storage
- Encrypted agent channels

Best for: Healthcare, financial data, PII

## Offline Mode

- 30-minute scratch memory TTL
- Local pinning only
- No remote storage
- Local agents only

Best for: Air-gapped environments, maximum security`,
      },
      {
        id: "agent-mesh",
        title: "Agent Mesh",
        content: `# Agent Mesh Architecture

## Overview

The Agent Mesh is a distributed network of AI agents that communicate through encrypted channels.

## Components

### Orchestrator
Coordinates task distribution and agent lifecycle.

### Executors
Specialized workers that perform specific computations.

### Validators
Verify computation results and ensure integrity.

### Observers
Monitor system health and collect metrics.

## Communication

All inter-agent communication is encrypted end-to-end. No agent can read another agent's data without explicit authorization.`,
      },
      {
        id: "memory-management",
        title: "Memory Management",
        content: `# Memory Management

## Scratch Memory

Temporary storage for computation intermediates. Automatically expires based on privacy mode TTL.

## Pinned Memory

Persistent storage for data you want to keep. Only available in Standard and Offline modes.

## Memory Locations

- **Device**: Stored locally, never leaves your machine
- **Redis**: Ephemeral server cache, encrypted at rest
- **Pinned**: Persistent storage with no TTL

## Best Practices

1. Use the shortest TTL that works for your use case
2. Pin only what you need to persist
3. Use High Sensitivity mode for PII
4. Burn data immediately when no longer needed`,
      },
    ],
  },
  {
    id: "api-reference",
    title: "API Reference",
    items: [
      {
        id: "authentication",
        title: "Authentication",
        content: `# Authentication

## API Keys

All API requests require authentication via API key.

\`\`\`bash
curl -H "Authorization: Bearer hvme_sk_..." \\
  https://api.hvme.ai/v1/sessions
\`\`\`

## Key Types

| Type | Prefix | Use Case |
|------|--------|----------|
| Secret Key | hvme_sk_ | Server-side only |
| Public Key | hvme_pk_ | Client-side safe |
| Test Key | hvme_test_ | Development |

## Security Best Practices

- Never expose secret keys in client code
- Rotate keys regularly
- Use environment variables
- Enable IP allowlisting for production`,
      },
      {
        id: "sessions",
        title: "Sessions API",
        content: `# Sessions API

## Create Session

\`\`\`http
POST /v1/sessions
Content-Type: application/json

{
  "privacyMode": "standard",
  "ttl": 3600
}
\`\`\`

### Response

\`\`\`json
{
  "id": "sess_abc123",
  "status": "ready",
  "privacyMode": "standard",
  "createdAt": "2026-01-29T12:00:00Z",
  "expiresAt": "2026-01-29T13:00:00Z"
}
\`\`\`

## Get Session

\`\`\`http
GET /v1/sessions/:id
\`\`\`

## Delete Session

\`\`\`http
DELETE /v1/sessions/:id
\`\`\`

Immediately destroys all session data and memory.`,
      },
      {
        id: "compute",
        title: "Compute API",
        content: `# Compute API

## Submit Task

\`\`\`http
POST /v1/compute
Content-Type: application/json

{
  "sessionId": "sess_abc123",
  "operation": "analyze",
  "input": { ... },
  "options": {
    "timeout": 30000,
    "priority": "normal"
  }
}
\`\`\`

### Response

\`\`\`json
{
  "taskId": "task_xyz789",
  "status": "pending",
  "estimatedTime": 5000
}
\`\`\`

## Get Task Status

\`\`\`http
GET /v1/compute/:taskId
\`\`\`

## Cancel Task

\`\`\`http
DELETE /v1/compute/:taskId
\`\`\``,
      },
    ],
  },
  {
    id: "sdks",
    title: "SDKs & Libraries",
    items: [
      {
        id: "typescript-sdk",
        title: "TypeScript SDK",
        content: `# TypeScript SDK

## Installation

\`\`\`bash
npm install @hvme/sdk
\`\`\`

## Basic Usage

\`\`\`typescript
import { HVME } from '@hvme/sdk';

const client = new HVME({
  apiKey: process.env.HVME_API_KEY,
});

// Create a session
const session = await client.createSession({
  privacyMode: 'standard',
});

// Run computation
const result = await session.compute({
  operation: 'classify',
  input: { text: 'Sample text' },
});

// Clean up
await session.destroy();
\`\`\`

## Type Safety

The SDK is fully typed with TypeScript definitions included.`,
      },
      {
        id: "python-sdk",
        title: "Python SDK",
        content: `# Python SDK

## Installation

\`\`\`bash
pip install hvme
\`\`\`

## Basic Usage

\`\`\`python
from hvme import HVME

client = HVME(api_key=os.environ["HVME_API_KEY"])

# Create a session
session = client.create_session(privacy_mode="standard")

# Run computation
result = session.compute(
    operation="classify",
    input={"text": "Sample text"}
)

# Clean up
session.destroy()
\`\`\`

## Async Support

\`\`\`python
import asyncio
from hvme import AsyncHVME

async def main():
    client = AsyncHVME(api_key=os.environ["HVME_API_KEY"])
    session = await client.create_session()
    result = await session.compute(...)
    await session.destroy()

asyncio.run(main())
\`\`\``,
      },
    ],
  },
];

// =============================================================================
// Search Component
// =============================================================================

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative mb-6">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-hvme-text-muted"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search documentation..."
        className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-hvme-bg-deep border border-hvme-border text-sm text-hvme-text-primary placeholder-hvme-text-muted focus:outline-none focus:border-hvme-primary/50 transition-colors"
      />
    </div>
  );
}

// =============================================================================
// Breadcrumbs Component
// =============================================================================

function Breadcrumbs({
  section,
  item,
}: {
  section: string;
  item: string;
}) {
  return (
    <nav className="flex items-center gap-2 text-sm text-hvme-text-muted mb-6">
      <a href="#" className="hover:text-hvme-primary transition-colors">Docs</a>
      <span>/</span>
      <span className="text-hvme-text-secondary">{section}</span>
      <span>/</span>
      <span className="text-hvme-text-primary">{item}</span>
    </nav>
  );
}

// =============================================================================
// Sidebar Component
// =============================================================================

function Sidebar({
  sections,
  activeItem,
  onSelect,
  searchQuery,
}: {
  sections: DocSection[];
  activeItem: string;
  onSelect: (id: string) => void;
  searchQuery: string;
}) {
  const [expanded, setExpanded] = useState<string[]>(sections.map((s) => s.id));

  const toggleSection = (sectionId: string) => {
    setExpanded((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Filter based on search
  const filteredSections = useMemo(() => {
    if (!searchQuery) return sections;
    const query = searchQuery.toLowerCase();
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.title.toLowerCase().includes(query) ||
            item.content.toLowerCase().includes(query)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [sections, searchQuery]);

  return (
    <nav className="w-full md:w-72 flex-shrink-0">
      <div className="sticky top-32 space-y-2">
        {filteredSections.map((section) => (
          <div key={section.id}>
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-hvme-text-primary hover:bg-hvme-bg-mid rounded-lg transition-colors"
            >
              {section.title}
              <motion.svg
                animate={{ rotate: expanded.includes(section.id) ? 180 : 0 }}
                className="w-4 h-4 text-hvme-text-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </motion.svg>
            </button>
            <AnimatePresence>
              {expanded.includes(section.id) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="ml-3 mt-1 space-y-1 border-l border-hvme-border pl-3">
                    {section.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => onSelect(item.id)}
                        className={`
                          w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors
                          ${activeItem === item.id
                            ? "text-hvme-primary bg-hvme-primary/10"
                            : "text-hvme-text-secondary hover:text-hvme-text-primary hover:bg-hvme-bg-mid"
                          }
                        `}
                      >
                        {item.title}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </nav>
  );
}

// =============================================================================
// Code Block Component with Syntax Highlighting
// =============================================================================

function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple syntax highlighting
  const highlightCode = (code: string, lang: string) => {
    // Keywords
    const keywords = /\b(import|export|from|const|let|var|function|async|await|return|if|else|for|while|class|interface|type|extends|implements|new|this|super|try|catch|throw|finally)\b/g;
    // Strings
    const strings = /(["'`])(?:(?!\1)[^\\]|\\.)*\1/g;
    // Comments
    const comments = /(\/\/.*$|\/\*[\s\S]*?\*\/|#.*$)/gm;
    // Numbers
    const numbers = /\b(\d+)\b/g;

    let highlighted = code
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    highlighted = highlighted
      .replace(comments, '<span class="text-hvme-text-muted">$1</span>')
      .replace(strings, '<span class="text-hvme-green">$&</span>')
      .replace(keywords, '<span class="text-hvme-primary">$1</span>')
      .replace(numbers, '<span class="text-hvme-gold">$1</span>');

    return highlighted;
  };

  return (
    <div className="relative group rounded-lg overflow-hidden bg-hvme-bg-deep border border-hvme-border my-4">
      {/* Language badge */}
      <div className="flex items-center justify-between px-4 py-2 bg-hvme-bg-mid/50 border-b border-hvme-border">
        <span className="text-xs font-mono text-hvme-text-muted">{language}</span>
        <button
          onClick={handleCopy}
          className="text-xs text-hvme-text-muted hover:text-hvme-primary transition-colors"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto">
        <code
          className="text-sm font-mono text-hvme-text-secondary"
          dangerouslySetInnerHTML={{ __html: highlightCode(code, language) }}
        />
      </pre>
    </div>
  );
}

// =============================================================================
// Content Renderer Component
// =============================================================================

function ContentRenderer({ content }: { content: string }) {
  const lines = content.trim().split("\n");
  const elements: JSX.Element[] = [];
  let i = 0;
  let codeBlock: { lang: string; code: string[] } | null = null;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    // Code block start/end
    if (line.startsWith("```")) {
      if (codeBlock) {
        // End code block
        elements.push(
          <CodeBlock
            key={`code-${i}`}
            code={codeBlock.code.join("\n")}
            language={codeBlock.lang}
          />
        );
        codeBlock = null;
      } else {
        // Start code block
        codeBlock = { lang: line.slice(3) || "text", code: [] };
      }
      i++;
      continue;
    }

    // Inside code block
    if (codeBlock) {
      codeBlock.code.push(line);
      i++;
      continue;
    }

    // Headers
    if (line.startsWith("# ")) {
      elements.push(
        <h1
          key={i}
          className="font-heading font-bold text-3xl text-hvme-text-primary mb-4 mt-8 first:mt-0"
        >
          {line.slice(2)}
        </h1>
      );
    } else if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={i}
          className="font-heading font-bold text-xl text-hvme-text-primary mb-3 mt-6"
        >
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith("### ")) {
      elements.push(
        <h3
          key={i}
          className="font-heading font-semibold text-lg text-hvme-text-primary mb-2 mt-4"
        >
          {line.slice(4)}
        </h3>
      );
    }
    // List items
    else if (line.startsWith("- ")) {
      elements.push(
        <li key={i} className="font-body text-hvme-text-secondary ml-4 mb-1 list-disc">
          {line.slice(2)}
        </li>
      );
    }
    // Table rows
    else if (line.startsWith("|")) {
      elements.push(
        <div
          key={i}
          className="font-mono text-sm text-hvme-text-secondary bg-hvme-bg-deep px-3 py-1 border-b border-hvme-border"
        >
          {line}
        </div>
      );
    }
    // Empty lines
    else if (line.trim() === "") {
      elements.push(<div key={i} className="h-2" />);
    }
    // Regular paragraphs
    else {
      elements.push(
        <p key={i} className="font-body text-hvme-text-secondary mb-3">
          {line}
        </p>
      );
    }

    i++;
  }

  return <div className="prose prose-invert max-w-none">{elements}</div>;
}

// =============================================================================
// Docs Page Component
// =============================================================================

export function DocsPage() {
  const [activeItem, setActiveItem] = useState("introduction");
  const [searchQuery, setSearchQuery] = useState("");
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true });

  // Find active content and section
  let activeContent = "";
  let activeSection = "";
  let activeTitle = "";
  for (const section of DOC_SECTIONS) {
    const item = section.items.find((i) => i.id === activeItem);
    if (item) {
      activeContent = item.content;
      activeSection = section.title;
      activeTitle = item.title;
      break;
    }
  }

  return (
    <div className="min-h-screen py-12">
      {/* Header with gradient */}
      <motion.div
        ref={headerRef}
        initial={{ opacity: 0, y: 20 }}
        animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="mx-auto max-w-7xl px-6 mb-8"
      >
        <h1
          className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-3"
          style={{
            background: "linear-gradient(135deg, #DCA0FA, #E6BEFA)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Documentation
        </h1>
        <p className="font-body text-hvme-text-secondary">
          Everything you need to integrate HVME into your applications.
        </p>
      </motion.div>

      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="md:w-72 flex-shrink-0">
            <SearchBar value={searchQuery} onChange={setSearchQuery} />
            <Sidebar
              sections={DOC_SECTIONS}
              activeItem={activeItem}
              onSelect={setActiveItem}
              searchQuery={searchQuery}
            />
          </div>

          {/* Main Content */}
          <motion.main
            key={activeItem}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex-1 min-w-0"
          >
            {/* Breadcrumbs */}
            <Breadcrumbs section={activeSection} item={activeTitle} />

            {/* Content */}
            <div className="p-8 rounded-2xl bg-hvme-bg-surface border border-hvme-border">
              <ContentRenderer content={activeContent} />
            </div>

            {/* Footer links */}
            <div className="mt-6 flex items-center justify-between text-sm">
              <a
                href="#"
                className="text-hvme-text-muted hover:text-hvme-primary transition-colors"
              >
                Edit this page on GitHub →
              </a>
              <span className="text-hvme-text-muted">Last updated: Jan 2026</span>
            </div>
          </motion.main>
        </div>
      </div>
    </div>
  );
}
